import { boolean, nativeEnum, object, string, z } from 'zod';

import { UUID_MATCHER } from '../common/matchers';
import { INVALID_UUID } from '../common/messages';

export type FeatureFlagsForCurrentAdmin = {
  ENABLE_READ_ONLY_MODE: boolean;
  ENABLE_TEAMS: boolean;
  ENABLE_COMMON_REPO_API: boolean;
  ENABLE_DOC_BETA_MODE: boolean;
};

export type FeatureFlag =
  | 'ALLOW_API_OWNER_APP_CHANGE'
  | 'ENABLE_SCOPE_OVERVIEW'
  | 'ENABLE_CLIENT_IMPORT'
  | 'ENABLE_READ_ONLY_MODE'
  | 'ENABLE_TEAMS'
  | 'ENABLE_COMMON_REPO_API'
  | 'ENABLE_DOC_BETA_MODE'
  | 'ENABLE_CUSTOM_PAGES_FOR_TEAMS'
  | 'ENABLE_NEW_CONSUMER_APP_CREATION'
  | 'ENABLE_ENV_COLOR_INDICATOR'
  | 'ENABLE_SEMANTIC_SEARCH'
  | 'ENABLE_TEAM_PAGES_OVERVIEW'
  | 'ENABLE_MCP_PAGES';

export type FeatureFlagType = 'organization' | 'team' | 'email' | 'everyone';

export enum FeatureFlags {
  ALLOW_API_OWNER_APP_CHANGE = 'ALLOW_API_OWNER_APP_CHANGE',
  ENABLE_SCOPE_OVERVIEW = 'ENABLE_SCOPE_OVERVIEW',
  ENABLE_CLIENT_IMPORT = 'ENABLE_CLIENT_IMPORT',
  ENABLE_READ_ONLY_MODE = 'ENABLE_READ_ONLY_MODE',
  ENABLE_TEAMS = 'ENABLE_TEAMS',
  ENABLE_DOC_BETA_MODE = 'ENABLE_DOC_BETA_MODE',
  ENABLE_COMMON_REPO_API = 'ENABLE_COMMON_REPO_API',
  ENABLE_CUSTOM_PAGES_FOR_TEAMS = 'ENABLE_CUSTOM_PAGES_FOR_TEAMS',
  ENABLE_NEW_CONSUMER_APP_CREATION = 'ENABLE_NEW_CONSUMER_APP_CREATION',
  ENABLE_ENV_COLOR_INDICATOR = 'ENABLE_ENV_COLOR_INDICATOR',
  ENABLE_SEMANTIC_SEARCH = 'ENABLE_SEMANTIC_SEARCH',
  ENABLE_TEAM_PAGES_OVERVIEW = 'ENABLE_TEAM_PAGES_OVERVIEW',
  ENABLE_MCP_PAGES = 'ENABLE_MCP_PAGES',
}

export const featureFlagDescriptions: Record<FeatureFlags, string> = {
  [FeatureFlags.ALLOW_API_OWNER_APP_CHANGE]:
    'Enables functionality in Admin Portal to update which app an API/event belongs to.',
  [FeatureFlags.ENABLE_SCOPE_OVERVIEW]: 'Gives access to the /scope-overview page on the Developer Portal.',
  [FeatureFlags.ENABLE_CLIENT_IMPORT]:
    'Allow users to import clients directly from the consumer app page and delete clients from the Portal. Should only be used by Developer Portal team members to manually solve client import issues.',
  [FeatureFlags.ENABLE_READ_ONLY_MODE]:
    'Allow only GET requests in the API and BFFs. Currently only configured for V1 endpoints.',
  [FeatureFlags.ENABLE_TEAMS]: 'Enable team functionality',
  [FeatureFlags.ENABLE_DOC_BETA_MODE]: 'Enable beta documentation features (Dark mode)',
  [FeatureFlags.ENABLE_COMMON_REPO_API]:
    'Gives access to the /api-contracts pages on the Developer Portal. Used to show the API contracts in the Common Contract Repository.',
  [FeatureFlags.ENABLE_CUSTOM_PAGES_FOR_TEAMS]: 'Allow teams to create and manage content pages.',
  [FeatureFlags.ENABLE_NEW_CONSUMER_APP_CREATION]:
    'Enables the updated consumer app creation flow, which requires a ServiceNow identifier. Should only be enabled for DNB users.',
  [FeatureFlags.ENABLE_ENV_COLOR_INDICATOR]:
    'Show a color indicator in the navigation banner. Should ONLY be enabled in lower environments. DO NOT enable in production.',
  [FeatureFlags.ENABLE_SEMANTIC_SEARCH]: 'Enable semantic search functionality',
  [FeatureFlags.ENABLE_TEAM_PAGES_OVERVIEW]:
    'Gives access to the /team-pages page on the Developer Portal, showing all team pages organized by team.',
  [FeatureFlags.ENABLE_MCP_PAGES]:
    'Gives access to the /mcps page on the Developer Portal, showing all MCPs organized by classification.',
};

export interface FeatureFlagDto {
  id: string;
  key: FeatureFlags;
  value: boolean;
  type: FeatureFlagType;
  typeId: string | null;
  name?: string;
}

export const featureFlagInputSchema = object({
  id: string().regex(UUID_MATCHER, INVALID_UUID).optional(),
  key: nativeEnum(FeatureFlags),
  value: boolean(),
  type: z.enum(['organization', 'team', 'email', 'everyone']),
  typeId: string().nullable(),
});

export type FeatureFlagInputDto = z.infer<typeof featureFlagInputSchema>;
